import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { NumberToCurrencyPipe } from '../../../../shared/pipes/number-to-currency.pipe';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { CardsStore } from '../../core/stores/CardsStore';
import { CardActions } from '../../core/enums/CardActions';
import { CardsService } from '../../core/services/CardsService';
import { ICard } from '../../core/interfaces/ICard';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { AlertModalComponent } from '../../../../shared/components/alert-modal/alert-modal.component';
import {
  ConfirmationModalComponent
} from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { CardTypeKeys } from '../../core/enums/CardTypeKeys';
import { LocaleDateFormatPipe } from '../../../../shared/pipes/locale-date-format.pipe';
import { CartStore } from '../../../cart/core/stores/CartStore';
import { CartService } from '../../../cart/core/services/CartService';
import { WorkerService } from '../../../worker/core/services/WorkerService';
import { finalize } from 'rxjs';
import {
  CustomersSearchModalComponent
} from '../../../customers/modals/customers-search-modal/customers-search-modal.component';
import { ICustomer } from '../../../customers/core/interfaces/ICustomer';
import { CardStatuses } from '../../core/enums/CardStatuses';
import { CustomerDetailsModalComponent } from '../../../customers/modals/customer-details-modal/customer-details-modal.component';
import { EmptyPipe } from "../../../../shared/pipes/empty.pipe";
import { CardTransactionsTableComponent } from "../../tables/card-transactions-table/card-transactions-table.component";
import { CardTicketsHistoryTableComponent } from "../../tables/card-tickets-history-table/card-tickets-history-table.component";
import { CardFreeEntriesTableComponent } from "../../tables/card-free-entries-table/card-free-entries-table.component";
import { CardProductsTableComponent } from "../../tables/card-products-table/card-products-table.component";
import { ActionsHistoryTableComponent } from "../../tables/actions-history-table/actions-history-table.component";
import { ToggleTabsMenuComponent } from "../../../../shared/components/toggle-tabs-menu/toggle-tabs-menu.component";
import { LoaderComponent } from "../../../../shared/components/loader/loader.component";
import { TooltipDirective } from '../../../../shared/directives/tooltip.directive';
import { TOOLTIP_DELAY } from '../../../../shared/constants/tooltip';

@Component({
  selector: 'app-card-details-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslatePipe,
    NumberToCurrencyPipe,
    CommonModule,
    LocaleDateFormatPipe,
    CustomersSearchModalComponent,
    CustomerDetailsModalComponent,
    EmptyPipe,
    CardTransactionsTableComponent,
    CardTicketsHistoryTableComponent,
    CardFreeEntriesTableComponent,
    CardProductsTableComponent,
    ActionsHistoryTableComponent,
    ToggleTabsMenuComponent,
    LoaderComponent,
    TooltipDirective
],
  templateUrl: './card-details-modal.component.html',
  styleUrl: './card-details-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CardDetailsModalComponent extends BaseModalComponent<{ card: ICard; }> {

  protected readonly tooltipDelay = TOOLTIP_DELAY;

  public selectedCustomer: ICustomer;
  public selectedToggleTabsMenuOptionsIndex = 0;
  public readonly isLoading = signal(false);
  public readonly isActionMaking = signal(false);
  public readonly isPrinting = signal(false);
  public readonly cardActions = CardActions;
  public readonly cardStatuses = CardStatuses;
  public readonly toggleTabsMenuOptions = [
    'Overview'.trans('cards/modals/card-details/tabs/overview/tab.Title'),
    'Action'.trans('cards/modals/card-details/tabs/action/tab.Title'),
    'Transaction positions'.trans('cards/modals/card-details/tabs/transation-positions/tab.Title'),
    'Ticket information'.trans('cards/modals/card-details/tabs/ticket-information/tab.Title'),
  ];
  public readonly customerCardCardType = computed(
    () =>
      this._cardsStore.allCardTypes().find(x => x.cardTypeId === this.data.card.cardTypeId)
  );

  private readonly _cardsStore = inject(CardsStore);
  private readonly _cardsService = inject(CardsService);
  private readonly _cartStore = inject(CartStore);
  private readonly _cartService = inject(CartService);
  private readonly _workerService = inject(WorkerService);

  public override open(val?: { card: ICard }): void {
    this.selectedCustomer = null;
    this.selectedToggleTabsMenuOptionsIndex = 0;
    this.isLoading.set(true);
    super.open(val);
    this.cdr.detectChanges();

    this._cardsService.getCardByNumber(this.data.card.cardNr)
      .pipe(
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: res => this.data.card = res.data,
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`get-card-by-number/notifications/${error.message}.Message`)
          );
        }
      });
  }
  
  public detachCardFromCart(): void {
    this._cartService.detachCardFromCart(this.data.card.cardId)
      .pipe(
        finalize(() => this.isActionMaking.set(false))
      )
      .subscribe({
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`detach-card/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public reprintCustomerCard(): void {
    if (!this.data?.card?.cardId || !this.data.card.canReprintCustomerCard || this.isPrinting() || this.isActionMaking()) {
      return;
    }

    this.isPrinting.set(true);
    this._workerService.printCard(this.data.card.cardId)
      .pipe(finalize(() => this.isPrinting.set(false)))
      .subscribe({
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`print-card/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public makeAction(action: CardActions): void {
    const makeAction = (): void => {
      this.isActionMaking.set(true);

      this._cardsService.makeAction(this.data.card.cardId, this.data.card.cardNr, action, this.selectedCustomer?.id)
        .subscribe({
          next: res => {
            this.data.card = res.data;
            this.cdr.detectChanges();

            if (this._cartStore.customerCards().some(card => card.cardNr === this.data.card.cardNr)) {
              if (
                action !== CardActions.CRD_PERSONALIZE &&
                action !== CardActions.CRD_UNPERSONALIZE
              ) {
                this.detachCardFromCart();
              } else {
                this._cartStore.cart.update(cart => {
                  const cardIndex = cart.customerCards?.findIndex(c => c.cardId === this.data.card.cardId) ?? -1;
                  if (cardIndex >= 0 && cart.customerCards) {
                    if (this.data.card.customer) {
                      cart.customerCards[cardIndex].customer = this.data.card.customer;
                      cart.customerCards[cardIndex].customerId = this.data.card.customerId;
                    } else {
                      cart.customerCards[cardIndex].customer = null;
                      cart.customerCards[cardIndex].customerId = null;
                    }
                  }

                  return {
                    ...cart,
                    customerCards: cart.customerCards
                  };
                });

                this.isActionMaking.set(false);
              }
            } else {
              this.isActionMaking.set(false);
            }
          },
          error: ({ error }: ApiErrorResponse) => {
            this.isActionMaking.set(false);
            this.notificationService.pushError(
              'Fehler'.trans('notifications/error.Title'),
              error.message.trans(`make-card-action/notifications/${error.message}.Message`)
            );
          }
        });
    };

    if (this.data.card.cardTypeKey === CardTypeKeys.CRD_EMPLOYEE) {
      if (action === CardActions.CRD_PERSONALIZE) {
        this.openAlertModal(
          'Personalize card'.trans(`card-details-modal/actions/${action}/employee-cards-cannot-be-personalized-alert-modal.Title`),
          'Employee cards cannot be personalized!'.trans(`card-details-modal/actions/${action}/employee-cards-cannot-be-personalized-alert-modal.Text`),
        );
      } else if (action === CardActions.CRD_DEACTIVATE) {
        this.openAlertModal(
          'Deactivate card'.trans(`card-details-modal/actions/${action}/employee-cards-cannot-be-deactivated-alert-modal.Title`),
          'Employee cards cannot be deactivated!'.trans(`card-details-modal/actions/${action}/employee-cards-cannot-be-deactivated-alert-modal.Text`),
        );
      }
    } else {
      if (action === CardActions.CRD_DEACTIVATE) {
        this.openConfirmationModal(
          'Deactivate card'.trans(`card-details-modal/actions/${action}/confirmation-modal.Title`),
          'Do you really want to deactivate the card? Warning! This action cannot be undone!'.trans(`card-details-modal/actions/${action}/confirmation-modal.Text`),
          makeAction
        );
      } else if (action === CardActions.CRD_EXPIRE) {
        this.openConfirmationModal(
          'Expire card'.trans(`card-details-modal/actions/${action}/confirmation-modal.Title`),
          'Do you really want to make the card as expired? Warning! This action cannot be undone!'.trans(`card-details-modal/actions/${action}/confirmation-modal.Text`),
          makeAction
        );
      } else if (action === CardActions.CRD_REMIT) {
        this.openConfirmationModal(
          'Remit card'.trans(`card-details-modal/actions/${action}/confirmation-modal.Title`),
          'Card [[cardNumber]] has a balance of [[balance]] [[currency]], [[freeEntries]] free entries, and [[bonusPoints]] bonus points. Would you still like to return the card?'
            .trans(`card-details-modal/actions/${action}/confirmation-modal.Text`, [['cardNumber', this.data.card.cardNr], ['balance', this.data.card.currentBalance.toCurrency()], ['currency', this.languageConfig.currency.short], ['freeEntries', this.data.card.freeEntries], ['bonusPoints', this.data.card.bonusPoints]]),
          makeAction
        );
      } else {
        makeAction();
      }
    }
  }

  private openAlertModal(title: string, text: string, onOkCb?: () => void): void {
    AlertModalComponent.openAlertModal(title, text, this.destroyRef, onOkCb);
  }

  private openConfirmationModal(title: string, text: string, onConfirmCb?: () => void): void {
    ConfirmationModalComponent.openConfirmationModal(title, text, this.destroyRef, onConfirmCb);
  }
}
